import React, { useState, useEffect } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Paper, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, useMediaQuery, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { academicAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const INITIAL = { name: '', type: '', description: '' };

const AcademicLevels = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.ACADEMIC.LEVELS, ACTIONS.READ);
  const canCreate = can(RESOURCES.ACADEMIC.LEVELS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.ACADEMIC.LEVELS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.ACADEMIC.LEVELS, ACTIONS.DELETE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [levels, setLevels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { if (canRead) fetchLevels(); }, [canRead]);

  const fetchLevels = async () => {
    try {
      const response = await academicAPI.getLevels();
      setLevels(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch levels', severity: 'error' });
    }
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setFormData({ name: level.name, type: level.type, description: level.description || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLevel) {
        await academicAPI.updateLevel(editingLevel.id, formData);
        setSnackbar({ open: true, message: 'Level updated successfully', severity: 'success' });
      } else {
        await academicAPI.createLevel(formData);
        setSnackbar({ open: true, message: 'Level created successfully', severity: 'success' });
      }
      resetForm();
      fetchLevels();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    }
  };

  const confirmDeleteAction = async () => {
    try {
      await academicAPI.deleteLevel(confirmDelete);
      setSnackbar({ open: true, message: 'Level deleted successfully', severity: 'success' });
      fetchLevels();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete level', severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const resetForm = () => { setFormData(INITIAL); setEditingLevel(null); setShowModal(false); };

  return (
    <Box>
      {!canRead && <Alert severity="error" sx={{ mb: 2 }}>You do not have permission to view levels.</Alert>}
      <ModulePageHeader
        title="Academic Levels & Years"
        subtitle="Manage education levels and years"
        actions={canCreate ? [
          <Button key="add-level" variant="contained" startIcon={<AddIcon />} onClick={() => setShowModal(true)} fullWidth={isMobile}>
            Add Level
          </Button>
        ] : []}
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Education Levels</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Level Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Classes Count</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {levels.map(level => (
                  <TableRow key={level.id}>
                    <TableCell>{level.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={level.type}
                        color={level.type === 'Primary' ? 'info' : level.type === 'Secondary' ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{level.description}</TableCell>
                    <TableCell>{level.classes_count || 0}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {canUpdate && (
                          <IconButton size="small" color="primary" onClick={() => handleEdit(level)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(level.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLevel ? 'Edit Academic Level' : 'Add Academic Level'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Level Name" value={formData.name} required
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Lower Primary, Upper Primary"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={['Primary', 'Secondary', 'Advanced']}
                  value={formData.type || null}
                  onChange={(_, value) => setFormData({ ...formData, type: value || '' })}
                  renderInput={(params) => <TextField {...params} label="Type" required />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Description" value={formData.description} multiline rows={2}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm}>Cancel</Button>
            <Button type="submit" variant="contained">{editingLevel ? 'Update Level' : 'Save Level'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Level</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this level? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AcademicLevels;
